import { type ReactNode, useState, useEffect, useRef, useCallback } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Sidebar, Text, Banner, Button, cn, useSidebar } from '@cloudflare/kumo'
import { PageHeaderSlotContext } from './kumo/page-header/page-header-context'
import {
  HouseIcon,
  WifiHighIcon,
  BuildingsIcon,
  UsersIcon,
  ClipboardTextIcon,
  ClockIcon,
  MoonIcon,
  SunIcon,
  SignOutIcon,
  UserCircleIcon,
  CaretDownIcon,
  CheckIcon,
  UserIcon,
  UserPlusIcon,
  WarehouseIcon,
  PrinterIcon,
  CalendarBlankIcon,
  ClockCounterClockwiseIcon,
  ScanSmileyIcon,
} from '@phosphor-icons/react'
import { useHasRole } from '../auth/useRoles'
import { useTranslation } from '../i18n'
import { useAppVersion } from '../version/AppVersionProvider'
import { CloseMobileDrawerOnNavigate, MobileTabBar, useShowMobileTabBar } from './MobileTabBar'

const SIDEBAR_ID = 'app-sidebar'

interface Props {
  children: ReactNode | ((props: { darkMode: boolean; onToggleDarkMode: () => void }) => ReactNode)
}

function SidebarLogo({ darkMode }: { darkMode: boolean }) {
  const { state, isMobile } = useSidebar()
  const collapsed = !isMobile && state === 'collapsed'

  // Fixed-size box so the logo never reflows between expanded/collapsed —
  // both variants render inside the same 32px-tall, left-aligned container
  // instead of relying on each <img>'s own intrinsic sizing.
  return (
    <div
      className="flex items-center shrink-0"
      style={{ height: 32, width: collapsed ? 32 : 'auto' }}
    >
      {collapsed ? (
        <img
          src={
            darkMode
              ? 'https://tesset.com/brand/favicon-05.svg'
              : 'https://tesset.com/brand/favicon-01.svg'
          }
          alt="TESSET"
          // h-8 is not in the precompiled standalone sheet — size via style.
          className="w-8"
          style={{ height: 32, maxHeight: 32 }}
        />
      ) : (
        <img
          src={
            darkMode
              ? 'https://tesset.com/brand/logo-dark.svg'
              : 'https://tesset.com/brand/logo-primary.svg'
          }
          alt="TESSET Hub"
          // h-8 is not in the precompiled standalone sheet — size via style.
          className="w-auto"
          style={{ height: 32, maxHeight: 32 }}
        />
      )}
    </div>
  )
}

/**
 * Phones hide the sidebar (its logo lives there), so the header carries the
 * brand instead, linking home. Once the page title pins into the header it
 * shrinks to the mark so the title keeps the room.
 */
function MobileHeaderLogo({ darkMode, pinned }: { darkMode: boolean; pinned: boolean }) {
  const { isMobile } = useSidebar()
  if (!isMobile) return null
  const src = pinned
    ? darkMode
      ? 'https://tesset.com/brand/favicon-05.svg'
      : 'https://tesset.com/brand/favicon-01.svg'
    : darkMode
      ? 'https://tesset.com/brand/logo-dark.svg'
      : 'https://tesset.com/brand/logo-primary.svg'
  return (
    <RouterLink
      to="/"
      data-testid="mobile-header-logo"
      className="flex items-center shrink-0"
      aria-label="TESSET Hub"
    >
      {/* h-7 is not in the precompiled standalone sheet — size via style. */}
      <img src={src} alt="" className="w-auto" style={{ height: 28, maxHeight: 28 }} />
    </RouterLink>
  )
}

function MobileSidebarTrigger() {
  const { isMobile } = useSidebar()
  // Coworkers/operators on phones open the drawer from the tab bar's More.
  const hasTabBar = useShowMobileTabBar()
  if (!isMobile || hasTabBar) return null
  return <Sidebar.Trigger />
}

function KeyboardShortcuts() {
  const { toggleSidebar } = useSidebar()

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault()
        toggleSidebar()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleSidebar])

  return null
}

interface UserDropdownProps {
  darkMode: boolean
  onToggleDarkMode: () => void
}

function UserDropdown({ darkMode, onToggleDarkMode }: UserDropdownProps) {
  const { logout, user } = useAuth0()
  const { t, locale, setLocale } = useTranslation()
  const navigate = useNavigate()
  // Hand-rolled disclosure. A Kumo DropdownMenu (Base UI) render-prop trigger
  // did not open reliably in production (the menu never mounted), so this uses
  // an explicit open state with outside-click / Esc handling. Classes are all
  // verified against the standalone sheet (no dead utilities).
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [open])

  return (
    <div ref={ref} className="relative" data-testid="user-dropdown">
      <button
        data-testid="user-dropdown-trigger"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-kumo-tint"
      >
        <UserCircleIcon size={20} className="text-kumo-subtle" />
        <span className="text-kumo-default">{user?.name ?? user?.email}</span>
        <CaretDownIcon size={14} className="text-kumo-subtle" />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="user-dropdown-content"
          className="absolute right-0 mt-2 rounded-md ring ring-kumo-line bg-kumo-elevated shadow-lg z-50"
          style={{ top: '100%', width: 224 }}
        >
          <div className="px-3 py-2 border-b border-kumo-line">
            <Text as="p" size="sm" DANGEROUS_className="font-medium">
              {user?.name}
            </Text>
            <Text as="p" variant="secondary" size="xs">
              {user?.email}
            </Text>
          </div>

          <div className="py-1">
            <button
              role="menuitem"
              data-testid="dropdown-profile"
              onClick={() => {
                navigate('/my/profile')
                setOpen(false)
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-kumo-tint text-left"
            >
              <UserIcon size={16} className="text-kumo-subtle" />
              {t('nav.profile')}
            </button>

            <div className="px-3 py-1.5">
              <Text as="p" variant="secondary" size="xs" DANGEROUS_className="font-medium mb-1">
                {t('profile.language')}
              </Text>
              <div className="flex flex-col">
                <button
                  role="menuitemradio"
                  aria-checked={locale === 'pt-BR'}
                  data-testid="dropdown-lang-ptbr"
                  onClick={() => {
                    setLocale('pt-BR')
                    setOpen(false)
                  }}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-kumo-tint text-left"
                >
                  <CheckIcon
                    size={14}
                    className={locale === 'pt-BR' ? 'text-kumo-default' : 'invisible'}
                  />
                  Portugues (Brasil)
                </button>
                <button
                  role="menuitemradio"
                  aria-checked={locale === 'en'}
                  data-testid="dropdown-lang-en"
                  onClick={() => {
                    setLocale('en')
                    setOpen(false)
                  }}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-kumo-tint text-left"
                >
                  <CheckIcon
                    size={14}
                    className={locale === 'en' ? 'text-kumo-default' : 'invisible'}
                  />
                  English
                </button>
              </div>
            </div>

            <button
              role="menuitem"
              data-testid="dropdown-darkmode"
              onClick={() => onToggleDarkMode()}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-kumo-tint text-left"
            >
              {darkMode ? (
                <SunIcon size={16} className="text-kumo-subtle" />
              ) : (
                <MoonIcon size={16} className="text-kumo-subtle" />
              )}
              {t('nav.darkMode')}
            </button>
          </div>

          <div className="border-t border-kumo-line py-1">
            <button
              role="menuitem"
              data-testid="dropdown-logout"
              onClick={() => {
                logout({ logoutParams: { returnTo: window.location.origin } })
                setOpen(false)
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-kumo-danger hover:bg-kumo-tint text-left"
            >
              <SignOutIcon size={16} />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Deliberately NOT a kumo Dialog: kumo is mocked in this component's test
// suite, so a real Dialog's "cannot be dismissed" behavior (no close button,
// Escape ignored, backdrop click ignored) would never actually be exercised
// by those tests — it would just prove the mock does what the mock does.
// This is plain, hand-rolled markup with no dismiss affordance and no
// Escape/backdrop-click handlers wired up at all, so the guarantee is a
// property of code the test suite genuinely runs.
function VersionBlockedOverlay({ t, reload }: { t: (key: string) => string; reload: () => void }) {
  return (
    <div
      data-testid="version-blocked-dialog"
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
    >
      <div className="rounded-md ring ring-kumo-line bg-kumo-elevated p-6 shadow-lg max-w-sm text-center">
        <Text as="p" DANGEROUS_className="font-medium mb-2">
          {t('version.blockedTitle')}
        </Text>
        <Text as="p" variant="secondary" size="sm" DANGEROUS_className="mb-4">
          {t('version.blockedBody')}
        </Text>
        <Button data-testid="version-blocked-reload" variant="primary" onClick={reload}>
          {t('version.reload')}
        </Button>
      </div>
    </div>
  )
}

export function Layout({ children }: Props) {
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('hub-wifi-dark-mode') === 'true'
  })
  const isAdmin = useHasRole('admin')
  const isAdminOrOperator = useHasRole('admin', 'operator')
  const { t } = useTranslation()
  const location = useLocation()
  const { state: versionState, showStaleNotice, dismiss, reload } = useAppVersion()
  const isVersionBlocked = versionState === 'blocked'
  // Defensive mutual exclusion: in the real provider `state` and
  // `showStaleNotice` can never disagree (showStaleNotice is only true while
  // state === 'stale'), but Layout enforces it here too rather than trusting
  // that invariant blindly.
  const showVersionStaleBanner = showStaleNotice && !isVersionBlocked

  const [pinned, setPinned] = useState(false)
  const [mainNode, setMainNode] = useState<HTMLElement | null>(null)
  const [barLeftNode, setBarLeftNode] = useState<HTMLElement | null>(null)
  const [barPinnedNode, setBarPinnedNode] = useState<HTMLElement | null>(null)

  const setMainRef = useCallback((node: HTMLElement | null) => setMainNode(node), [])
  const setBarLeftRef = useCallback((node: HTMLElement | null) => setBarLeftNode(node), [])
  const setBarPinnedRef = useCallback((node: HTMLElement | null) => setBarPinnedNode(node), [])

  // Layout persists across route changes (App.tsx mounts it at the same tree
  // position per route), so the pinned state and main scroll position must be
  // reset explicitly when navigating — otherwise a scrolled-down page would
  // hand off a "pinned" header to the next page.
  useEffect(() => {
    setPinned(false)
    mainNode?.scrollTo({ top: 0 })
  }, [location.pathname, mainNode])

  const toggleDarkMode = () => {
    const next = !darkMode
    localStorage.setItem('hub-wifi-dark-mode', String(next))
    setDarkMode(next)
  }

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  const groups = [
    {
      label: null,
      items: [{ to: '/', label: t('nav.home'), icon: HouseIcon, show: true }],
    },
    {
      label: t('nav.groupMySpace'),
      items: [
        { to: '/my/wifi', label: t('nav.wifi'), icon: WifiHighIcon, show: true },
        {
          to: '/my/guests',
          label: t('nav.guestInvitations'),
          icon: UserPlusIcon,
          show: true,
        },
        { to: '/my/bookings', label: t('nav.bookings'), icon: CalendarBlankIcon, show: true },
        { to: '/my/print', label: t('nav.printCenter'), icon: PrinterIcon, show: true },
        { to: '/my/profile', label: t('nav.profile'), icon: UserIcon, show: true },
      ],
    },
    {
      label: t('nav.groupManage'),
      items: [
        {
          to: '/companies',
          label: t('nav.companies'),
          icon: BuildingsIcon,
          show: isAdminOrOperator,
        },
        { to: '/coworkers', label: t('nav.coworkers'), icon: UsersIcon, show: isAdminOrOperator },
        {
          to: '/schedules',
          label: t('nav.schedules'),
          icon: ClockIcon,
          show: isAdmin,
        },
        {
          to: '/audit-log',
          label: t('nav.auditLog'),
          icon: ClipboardTextIcon,
          show: isAdmin,
        },
        {
          to: '/access/activity',
          label: t('nav.accessActivity'),
          icon: ClockCounterClockwiseIcon,
          show: isAdminOrOperator,
        },
        {
          to: '/access/faces',
          label: t('nav.faceStatus'),
          icon: ScanSmileyIcon,
          show: isAdmin,
        },
      ],
    },
    {
      label: t('nav.groupInfra'),
      items: [
        {
          to: '/buildings',
          label: t('nav.buildings'),
          icon: WarehouseIcon,
          show: isAdminOrOperator,
        },
        { to: '/wifi-networks', label: t('nav.wifiNetworks'), icon: WifiHighIcon, show: isAdmin },
        {
          to: '/admin/printers',
          label: t('nav.printers'),
          icon: PrinterIcon,
          show: isAdminOrOperator,
        },
      ],
    },
  ]

  const renderedChildren =
    typeof children === 'function'
      ? children({ darkMode, onToggleDarkMode: toggleDarkMode })
      : children

  return (
    <div
      data-mode={darkMode ? 'dark' : 'light'}
      className="bg-kumo-base text-kumo-default flex"
      // viewport-fit=cover (index.html) lets the page run under a landscape
      // notch; keep the sidebar and header inside the safe area.
      style={{
        height: '100dvh',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
    >
      <Sidebar.Provider
        defaultOpen
        collapsible="icon"
        peekable
        resizable
        defaultWidth={200}
        minWidth={180}
        maxWidth={320}
      >
        <KeyboardShortcuts />
        <CloseMobileDrawerOnNavigate />
        <Sidebar
          id={SIDEBAR_ID}
          data-testid="sidebar"
          className="border-r border-kumo-line"
          style={{ height: '100vh' }}
        >
          <Sidebar.Header
            data-testid="sidebar-header"
            className="flex items-center px-3 border-b border-kumo-line"
            style={{ height: 58, minHeight: 58, maxHeight: 58 }}
          >
            <SidebarLogo darkMode={darkMode} />
          </Sidebar.Header>

          <Sidebar.Content>
            {groups
              .map((g) => ({ ...g, items: g.items.filter((i) => i.show) }))
              .filter((g) => g.items.length > 0)
              .map((g, idx) => (
                <Sidebar.Group key={g.label ?? `group-${idx}`}>
                  {g.label && <Sidebar.GroupLabel>{g.label}</Sidebar.GroupLabel>}
                  <Sidebar.Menu>
                    {g.items.map((item) => (
                      <Sidebar.MenuButton
                        key={item.to}
                        href={item.to}
                        active={isActive(item.to)}
                        icon={item.icon}
                      >
                        {item.label}
                      </Sidebar.MenuButton>
                    ))}
                  </Sidebar.Menu>
                </Sidebar.Group>
              ))}
          </Sidebar.Content>

          <Sidebar.Footer>
            <Sidebar.Trigger />
          </Sidebar.Footer>
        </Sidebar>

        <PageHeaderSlotContext.Provider
          value={{ pinned, setPinned, mainNode, barLeftNode, barPinnedNode }}
        >
          <div className="flex-1 flex flex-col overflow-hidden" style={{ height: '100dvh' }}>
            <header
              data-testid="main-header"
              className="flex items-center gap-3 px-4 border-b border-kumo-line bg-kumo-elevated shrink-0"
              style={{ height: 58, minHeight: 58, maxHeight: 58 }}
            >
              <MobileSidebarTrigger />
              <MobileHeaderLogo darkMode={darkMode} pinned={pinned} />
              <div
                ref={setBarLeftRef}
                className={cn(
                  'flex-1 min-w-0 overflow-hidden hidden sm:flex items-center',
                  pinned && 'sm:hidden',
                )}
              />
              <div
                ref={setBarPinnedRef}
                className={cn('flex-1 min-w-0 items-center gap-2', pinned ? 'flex' : 'hidden')}
              />
              <div className={cn('ml-auto flex items-center gap-2 shrink-0', pinned && 'hidden')}>
                <UserDropdown darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
              </div>
            </header>
            <main
              ref={setMainRef}
              className="w-full grow overflow-y-auto bg-kumo-canvas"
              style={{ boxShadow: '0 100px var(--color-kumo-canvas)' }}
            >
              <div className="mx-auto w-full px-4 py-8" style={{ maxWidth: 1280 }}>
                {showVersionStaleBanner && (
                  <Banner data-testid="version-stale-banner" variant="default">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <Text as="p">{t('version.staleBody')}</Text>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          data-testid="version-dismiss"
                          variant="secondary"
                          size="sm"
                          onClick={dismiss}
                        >
                          {t('version.dismiss')}
                        </Button>
                        <Button
                          data-testid="version-reload"
                          variant="primary"
                          size="sm"
                          onClick={reload}
                        >
                          {t('version.reload')}
                        </Button>
                      </div>
                    </div>
                  </Banner>
                )}
                {isVersionBlocked && <VersionBlockedOverlay t={t} reload={reload} />}
                {renderedChildren}
              </div>
            </main>
            <MobileTabBar drawerId={SIDEBAR_ID} />
          </div>
        </PageHeaderSlotContext.Provider>
      </Sidebar.Provider>
    </div>
  )
}
